<template>
  <div v-if="commande">
    <div class="entete">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3"> {{ getTitle }} </h1>
              <app-breadcrumb :breadcrumb="breadcrumb"/>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="buttons">
            <button class="button" @click="$router.go(-1)">
              {{ $t('general.button.cancel')}}
            </button>
            <button class="button is-addOrEdit" @click="save()">
              {{ $t('general.button.sauvegarder') }}
            </button>
            <piece-jointe-button
              class="mgr-2"
              :perm="permission.maj">
            </piece-jointe-button>
          </div>
        </div>
      </div>
    </div>
    <error-msg-component
      v-if="error"
      :error="error"
      :messages="messages"
      :champs-vide="champsVide"
      :messages-champs-vide="messagesChampsVide"
      @close="error = $event"
      class="mgt-20px"
      title="general.problemOccurred"
      withEmplyFields/>
    <div class="tab" v-if="tabs.length > 0">
      <ul>
        <li
          v-for="(tab, index) in tabs" :key="index"
          :class="{'is-active': activeTab === tab}"
          @click="activeTab = tab">
          <b-field>
            <div v-if="tab === 'rapprochementsTab'"
              class="mgv-auto mgr-5px"
              :class="getColorStatusRapprochement"></div>
            <span>{{ $t(`${prefixTrad}.tabs.${tab}`) }}</span>
          </b-field>
        </li>
      </ul>
    </div>
    <section class="corps">
      <component
        :is="activeTab"
        :detail="false"
        :showErrorDate="false"
        :dcf="commande"
        :commande="commande"
        @searchLigneCommandeARapprocher="searchLigneCommandeARapprocher"
        @typeProduitChange="resetLigneCommandeARapprocher()"
        @tiersUpdated="resetLigneCommandeARapprocher(true)"/>
    </section>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { createNamespacedHelpers } from 'vuex';
import {
  get,
  create,
  update,
} from '@/api/commandeAchat';
import {
  get as getBonReception,
  create as createBonReception,
  update as updateBonReception,
  getAllForRapprochement,
} from '@/api/bonReception';
import messageErreur from '@/services/general/messageErreur';
import messageSucces from '@/services/general/messageSucces';
import validationGeneral, { removeDuplicateMsgError } from '@/services/validation/validation';
import validationDevisCommande from '@/services/devisCommandes/validation/validation';
import validationLignesCommande from '@/services/dcf/validation/validationVentePartielle';
import validationLigneEcheance from '@/services/devisCommandes/validation/validationLigneEcheance';
import validationRapprochement from '@/services/achat/bonReception/validationRapprochement';
import validationLieuLivraison from '@/services/achat/lieuDeLivraison/validationLieuLivraison';
import validationCommandeAchat from '@/services/achat/commandes/validationCommandeAchat';
import translator from '@/services/devisCommandes/validation/champsCommandeTranslator';
import genererIntervenant from '@/services/devisCommandes/genererIntervenant';
import genererDevisCommande from '@/services/devisCommandes/genererDevisCommande';
import initializeCommandeDevis from '@/services/devisCommandes/initializeCommandeDevis';
import preparationEnvoiBack from '@/services/devisCommandes/preparationEnvoiBack/preparationEnvoiBack';
import {
  initializeRapprochementsLignesCommande,
  getRapprochementsLignesCommande,
  gestionRapprochementLigne,
} from '@/services/achat/bonReception/rapprochementServices';
import * as devisConf from '@/configuration/devisConf';
import { COMMANDES, BON_RECEPTION } from '@/configuration/achat/permissionsConf';
import AppBreadcrumb from '@/components/general/breadcrumb';
import ErrorMsgComponent from '@/components/general/errorMsgComponent';
import InformationsTab from '@/components/achat/commande/addEdit/informationsTab';
import RapprochementsTab from '@/components/achat/commande/addEdit/rapprochementsTab';
import LieuDeLivraisonTab from '@/components/achat/commande/addEdit/lieuDeLivraisonTab';
import PieceJointeButton from '@/components/general/button/pieceJointeButton';
import { TYPE_REFERENCE_EXTERNE_VENTE } from '@/configuration/dcf/dcfConf';

const Societe = createNamespacedHelpers('societe');
const Attachments = createNamespacedHelpers('attachments');

export default {
  name: 'MCommandeAchatFournisseur',
  components: {
    InformationsTab,
    AppBreadcrumb,
    ErrorMsgComponent,
    PieceJointeButton,
    RapprochementsTab,
    LieuDeLivraisonTab,
  },
  data() {
    return {
      commande: null,
      code: '',
      error: false,
      messages: [],
      nomCommercial: '',
      champsVide: [],
      messagesChampsVide: [],
      activeTab: 'informationsTab',
    };
  },
  computed: {
    ...Societe.mapGetters({
      agencesSelectionnees: 'agenceActive',
      societeActiveId: 'societeActiveId',
      societeActive: 'societeActive',
    }),
    isBonReception() {
      return this.$route.name === 'm.achat.bon_reception';
    },
    prefixTrad() {
      return this.isBonReception ? 'addEditBonReception' : 'addEditCommande';
    },
    routeName() {
      return this.isBonReception ? 'bon_reception' : 'commandes';
    },
    permission() {
      return this.isBonReception ? BON_RECEPTION : COMMANDES;
    },
    pageTrad() {
      return this.isBonReception ? 'bonReception' : 'commandes';
    },
    tabs() {
      const tabs = [
        { name: 'informationsTab', show: true },
        { name: 'lieuDeLivraisonTab', show: !this.isBonReception },
        { name: 'rapprochementsTab', show: this.isBonReception },
      ];
      return tabs.filter((tab) => tab.show).map((tab) => tab.name);
    },
    dataAlreadyExist() {
      return this.commande && !!(this.commande.id);
    },
    getTitle() {
      return this.dataAlreadyExist
        ? this.$t(`${this.prefixTrad}.title.edit`) + ' ' + this.code
        : this.$t(`${this.prefixTrad}.title.add`);
    },
    breadcrumb() {
      return [
        { title: this.$i18n.t('general.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$i18n.t(`${this.prefixTrad}.breadcrumb.liste`), route: { name: `l.achat.${this.routeName}` }, isActive: false },
        { title: this.dataAlreadyExist ? this.$i18n.t(`${this.prefixTrad}.breadcrumb.edit`) : this.$i18n.t(`${this.prefixTrad}.breadcrumb.add`), route: { name: '' }, isActive: true },
      ];
    },
    getColorStatusRapprochement() {
      const rapprochements = this.commande.lignesCommande
        .flatMap((ligne) => ligne.rapprochements);
      if (rapprochements.length === 0) return 'round-circle-danger';
      const quantiteAffecter = rapprochements
        .map((r) => r?.quantite)
        .reduce((a, b) => a + b);
      const quantiteARapprocher = this.commande.lignesCommande
        .map((ligne) => ligne.quantite)
        .reduce((a, b) => a + b);
      if (quantiteAffecter === quantiteARapprocher && quantiteARapprocher > 0) {
        return 'round-circle-success';
      }
      if (quantiteAffecter < quantiteARapprocher || quantiteARapprocher === 0) {
        return 'round-circle-warning';
      }
      return 'round-circle-danger';
    },
  },
  methods: {
    ...Attachments.mapActions({
      insertAttachments: 'insert',
      updateAttachments: 'update',
      setAttachmentContext: 'setContext',
      setAttachmentElementId: 'setElementId',
    }),
    async resetLigneCommandeARapprocher(fournisseurChanged = false) {
      if (!this.isBonReception) return;
      if (fournisseurChanged) {
        this.commande.rapprochements = [];
        this.commande.lignesCommande.forEach((ligne) => { ligne.rapprochements = []; });
      }
      await this.loadLigneCommandeARapprocher(this.commande);
    },
    async searchLigneCommandeARapprocher(search) {
      if (!this.isBonReception) return;
      await this.loadLigneCommandeARapprocher(this.commande, search);
    },
    async loadLigneCommandeARapprocher(commande, search) {
      const tiersId = [commande.intervenants[0]?.tiersId].filter(Boolean);
      const produitsIds = commande.lignesCommande
        .filter((ligne) => !!ligne.produitId)
        .map((ligne) => ligne.produitId);
      if (!tiersId.length || !produitsIds.length) return;
      const loadingComponent = this.$buefy.loading.open();
      try {
        const resultLignes = (await getAllForRapprochement({
          auto: search,
          societeId: this.societeActiveId,
          produitIds: produitsIds,
          tiersIds: tiersId,
        })).data.result;
        gestionRapprochementLigne(commande, resultLignes);
      } catch (e) {
        messageErreur(this.$i18n);
        throw e;
      } finally {
        loadingComponent.close();
      }
    },
    async save() {
      this.error = false;
      this.messages = [];
      this.champsVide = [];
      this.messagesChampsVide = [];
      this.error = this.messages.length > 0;
      if (this.error) return;
      const loadingComponent = this.$buefy.loading.open();
      try {
        let commandeCopy = preparationEnvoiBack(this.commande);
        commandeCopy.rapprochements = getRapprochementsLignesCommande(commandeCopy);
        if (!this.dataAlreadyExist) {
          const createFuncAsync = this.isBonReception ? createBonReception : create;
          commandeCopy.societeId = this.societeActiveId;
          commandeCopy = (await createFuncAsync(commandeCopy)).data.result;
          await this.insertAttachments(commandeCopy.id);
          messageSucces(this.$i18n.t(`${this.prefixTrad}.notifications.successCreation`));
        } else {
          const updateFuncAsync = this.isBonReception ? updateBonReception : update;
          commandeCopy = (await updateFuncAsync(commandeCopy)).data.result;
          await this.updateAttachments(commandeCopy.id);
          messageSucces(this.$i18n.t(`${this.prefixTrad}.notifications.successChange`));
        }
        this.$router.push({
          name: `c.achat.${this.routeName}`,
          params: { id: commandeCopy.id },
        });
      } catch (e) {
        this.error = true;
        if (e.response?.data?.error) {
          validationDevisCommande(e.response,
            this.messages,
            !this.dataAlreadyExist,
            devisConf.TYPE_COMMANDE);
          await validationLignesCommande(e.response, this.messages, this.commande);
          validationLigneEcheance(e.response, this.messages,
            this.commande.lignesCommande, devisConf.TYPE_COMMANDE);
          validationGeneral(this.$i18n, e.response, this.champsVide,
            this.messages, this.messagesChampsVide, translator);
          validationRapprochement(e.response, this.messages);
          validationLieuLivraison(e.response, this.messages);
          validationCommandeAchat(e.response, this.messages);
          this.messages = removeDuplicateMsgError(this.messages);
        } else messageErreur(this.$i18n);
        throw e;
      } finally {
        loadingComponent.close();
      }
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.setAttachmentContext({ module: 'Commande' });
      let commandeGet = genererDevisCommande(
        devisConf.TYPE_COMMANDE,
        this.isBonReception ? devisConf.SOUS_TYPE_BON_RECEPTION : devisConf.SOUS_TYPE_NORMAL,
        this.societeActive,
        devisConf.SENS_ACHAT,
        devisConf.MODULE_ACHAT,
      );
      if (this.$route.params.id) {
        const getFuncAsync = this.isBonReception ? getBonReception : get;
        commandeGet = (await getFuncAsync(this.$route.params.id)).data.result;
        initializeCommandeDevis(commandeGet);
        commandeGet.lignesCommande.forEach((ligne) => { ligne.selected = false; });
        this.code = cloneDeep(commandeGet.code);
        if (this.isBonReception) initializeRapprochementsLignesCommande(commandeGet);
        this.setInformationPage(this.$t('pageGeneral.modification', { item: this.code }));
        await this.setAttachmentElementId({ id: commandeGet.id });
      } else {
        const intervenant = genererIntervenant();
        intervenant.repartition = 100;
        intervenant.destinataire = true;
        intervenant.annonceur = true;
        intervenant.payeur = true;
        commandeGet.rapprochements = [];
        commandeGet.intervenants.push(intervenant);
        if (this.agencesSelectionnees.length === 1) {
          commandeGet.agenceId = this.agencesSelectionnees[0].agenceId;
        }
        commandeGet.aRapprocher = true;
        if (this.$route.params.referenceExterne) {
          commandeGet.referenceExterne = this.$route.params.referenceExterne;
          commandeGet.typeReferenceExterne = TYPE_REFERENCE_EXTERNE_VENTE;
        }
        this.setInformationPage(this.$t(`pageAchat.${this.pageTrad}.creation`));
      }
      commandeGet.echeancierPaiement = [];
      if (this.isBonReception) await this.loadLigneCommandeARapprocher(commandeGet);
      this.commande = cloneDeep(commandeGet);
    } catch (e) {
      messageErreur(this.$i18n);
      throw e;
    } finally {
      loadingComponent.close();
    }
  },
};
</script>
